import React, { useEffect, useState } from 'react';
import AdminLayout from '../../../components/layout/AdminLayout';
import Card from '../../../components/common/Card';
import EndorsementMotivationTable from '../../../components/tables/EndorsementMotivationTable';
import Button from '../../../components/common/Button';
import FormField from '../../../components/common/FormField';
import { useEndorsementMotivationSearch, useDeleteEndorsementMotivation } from '../../../hooks/useEndorsementMotivationApi';
import AddNewButton from '../../../components/common/AddButton';
import { confirmDelete } from '../../../utils/sweetAlertUtils';
import { EndorsementMotivationDTO } from '../../../services/apiClient';
import { useNavigate } from 'react-router-dom';

const EndorsementMotivationList: React.FC = () => {
  const { search } = useEndorsementMotivationSearch();
  const deleteMutation = useDeleteEndorsementMotivation();
  const [isLoading, setIsLoading] = useState(false);
  const [items, setItems] = useState<EndorsementMotivationDTO[]>([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);
  const [searchFilters, setSearchFilters] = useState<any>({});

  const navigate = useNavigate();
  const breadcrumbs = [
    { label: 'Admin', href: '#' },
    { label: 'Endorsement Motivations', href: '#' },
  ];

  const fetchItems = async (page: number, size: number, filters: any) => {
    setIsLoading(true);
    try {
      const response = await search({
        filters,
        page,
        pageSize: size,
        sortBy: 'CreatedUtc',
        sortDescending: true,
      });
      setItems(response?.results || []);
      setTotalRecords(response?.totalCount || 0);
    } catch (error) {
      console.error('Error fetching Endorsement Motivations:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchItems(currentPage, pageSize, {});
  }, [currentPage, pageSize]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchItems(1, pageSize, searchFilters);
  };

  const handleClearSearch = () => {
    setSearchFilters({});
    fetchItems(1, pageSize, {});
  };

  const handleDelete = async (dto: EndorsementMotivationDTO) => {
    const confirmed = await confirmDelete();
    if (!confirmed) return;

    try {
      await deleteMutation.mutateAsync(dto.id || '');
      await fetchItems(currentPage, pageSize, {});
    } catch (error) {
      console.error('Error deleting EndorsementMotivation:', error);
    }
  };

  return (
    <AdminLayout
      activeMenuItem="/admin/endorsementmotivations"
      breadcrumbs={breadcrumbs}
      allowedRoles={['Admin']}
      requiredPermission="Settings.EndorsementMotivation.View"
    >
      <Card
        title="Manage Endorsement Motivations"
        subtitle={`Total Records: ${totalRecords}`}
        headerActions={
          <AddNewButton
            onClick={() => navigate('/admin/endorsementmotivations/create')}
          />
        }
      >
        <form onSubmit={handleSearch} className="mb-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <FormField
              id="title"
              label="Search by Title"
              value={searchFilters.title || ''}
              onChange={(e) =>
                setSearchFilters({ ...searchFilters, title: e.target.value })
              }
              placeholder="Search by Title"
            />
          </div>

          <div className="flex space-x-4 mb-4">
            <Button variant="secondary" onClick={handleClearSearch}>
              Clear Search
            </Button>
            <Button type="submit">Search</Button>
          </div>
        </form>

        <EndorsementMotivationTable
          data={items}
          handleEdit={(dto) =>
            navigate(`/admin/endorsementmotivations/${dto.id}`)
          }
          handleDelete={handleDelete}
          currentPage={currentPage}
          totalPages={Math.ceil(totalRecords / pageSize)}
          setCurrentPage={setCurrentPage}
          pageSize={pageSize}
          setPageSize={setPageSize}
          isLoading={isLoading}
        />
      </Card>
    </AdminLayout>
  );
};

export default EndorsementMotivationList;
